import React, { forwardRef } from 'react';
import { styled } from '@compiled/react';
import type { PopupComponentProps } from '@atlaskit/popup';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';

const OPTION_COMPACT_HEIGHT = 28;
const OPTION_DEFAULT_HEIGHT = 40;
const INPUT_HEIGHT = 48;
const FOOTER_HEIGHT = 46;
const MENU_LIST_HEIGHT = 300;
const GROUP_PADDING = 6;
const ACTION_OPTION_HEIGHT = 42;

export const PopupComponent = forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => (
	<PopupComponentContainer {...props} ref={ref} />
));

export const getPopupComponentWithHeight = ({
	numOptions,
	hasInput = true,
	hasFooter = false,
	spacing = 'compact',
	numGroups = 0,
	numActionOptions = 0,
}: {
	numOptions: number;
	hasInput?: boolean;
	hasFooter?: boolean;
	spacing?: 'compact' | 'default';
	numGroups?: number;
	numActionOptions?: number;
}) => {
	const [expConfig] = UNSAFE_noExposureExp('jira_inline_create_status_list_view');
	const isInlineCreateStatusEnabled = expConfig.get('isInlineCreateStatusEnabled', false);

	const newGroupAndActionOptions = isInlineCreateStatusEnabled
		? numGroups * GROUP_PADDING + numActionOptions * ACTION_OPTION_HEIGHT
		: 0;
	const menuHeight =
		numOptions * (spacing === 'compact' ? OPTION_COMPACT_HEIGHT : OPTION_DEFAULT_HEIGHT) +
		newGroupAndActionOptions;

	const menuAdjustedHeight = Math.min(menuHeight, MENU_LIST_HEIGHT);

	const height =
		menuAdjustedHeight + (hasInput ? INPUT_HEIGHT : 0) + (hasFooter ? FOOTER_HEIGHT : 0);

	return forwardRef<HTMLDivElement, PopupComponentProps>((props, ref) => (
		<PopupComponentContainer {...props} ref={ref} height={height} />
	));
};

const shadowSize = 2;
const boxShadow = `0 0 0 ${shadowSize}px ${token('color.border.focused', colors.B200)} inset`;
const transition = 'box-shadow 0.1s cubic-bezier(0.47, 0.03, 0.49, 1.38)';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PopupComponentContainer = styled.div<{ height?: number }>({
	backgroundColor: 'transparent',
	transition,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	height: ({ height }) => (height != null ? `${height}px` : 'inherit'),
	'&:focus': {
		outline: 'none',
		boxShadow,
	},
});
